// src/redux/modules/auth/services/authApi.slice.ts
import { baseApi } from '@/rtkQuery/features/services/baseApi';
import { buildQuery, handleAuthError } from '@/rtkQuery/lib';
import type {
    PatientEntitlementsResponse,
    PatientInformationPostResponse,
    PatientInformationRequest,
    PatientInformationResponse,
} from '@/rtkQuery/features/models/patientInformation.model';
import { PATIENT_INFORMATION_ENDPOINTS } from '@/rtkQuery/config/patientInformation';

export const patientInformationApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        postPatientInformation: builder.mutation<PatientInformationPostResponse, PatientInformationRequest>({
            query: buildQuery<PatientInformationRequest>(
                PATIENT_INFORMATION_ENDPOINTS.POST_PATIENT_INFORMATION,
                'POST'
            ),
            transformResponse: (response: unknown, meta: any, _arg: PatientInformationRequest): PatientInformationPostResponse => {
                meta.status = 'success';
                meta.error = null;
                return response as PatientInformationPostResponse;
            },
            transformErrorResponse: (response, meta) => {
                const errorMessage =
                    (typeof response.data === 'object' && response.data && 'Message' in response.data ? (response.data as any).Message : undefined) ||
                    (typeof response.data === 'object' && response.data && 'message' in response.data ? (response.data as any).message : undefined) ||
                    'Failed to submit contact form';
                (meta as any).status = 'error';
                (meta as any).error = errorMessage;
                return {
                    status: response.status,
                    message: errorMessage,
                    errors: response.data && typeof response.data === 'object' && response.data !== null && 'errors' in response.data ? response.data.errors : {},
                };
            },

            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    patientInformationApi.util.updateQueryData(
                        'getPatientInformation',
                        undefined,
                        (draft) => {
                            if (!draft) return;

                            draft.FullName = `${arg.firstName} ${arg.lastName}`.trim();
                            draft.DateOfBirth = arg.dateOfBirth?.toString();
                            draft.PhoneNumber = arg.phoneNumber;
                            draft.Email = arg.email;
                            draft.Address = arg.address;
                            draft.Address2 = arg.address2 ?? null;
                            draft.City = arg.city;
                            draft.State = arg.state;
                            draft.ZipCode = arg.zipCode;
                        }
                    )
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },

            invalidatesTags: ['PatientInfo'],
        }),


        getPatientInformation: builder.query<PatientInformationResponse, void>({
            query: buildQuery<void>(PATIENT_INFORMATION_ENDPOINTS.GET_PATIENT_INFORMATION, 'GET'),
            transformResponse: (response: PatientInformationResponse, meta) => {
                (meta as any).status = 'success';
                (meta as any).error = null;
                return response;
            },
            transformErrorResponse: (response: any, meta) => {
                const errorMessage = handleAuthError({
                    message: response.data || response.data?.message,
                    status: response.status,
                });
                (meta as any).status = 'error';
                (meta as any).error = errorMessage;
                return {
                    status: response.status,
                    message: errorMessage,
                    errors: response.data || response.data?.errors || {},
                };
            },
            providesTags: ['PatientInfo'],
        }),
        getPatientEntitlements: builder.query<PatientEntitlementsResponse, void>({
            query: buildQuery<void>(PATIENT_INFORMATION_ENDPOINTS.GET_PATIENT_ENTITLEMENTS, 'GET'),
            transformResponse: (response: PatientEntitlementsResponse, meta) => {
                (meta as any).status = 'success';
                (meta as any).error = null;
                return response;
            },
            transformErrorResponse: (response: any, meta) => {
                const errorMessage = handleAuthError({
                    message: response.data || response.data?.message,
                    status: response.status,
                });
                (meta as any).status = 'error';
                (meta as any).error = errorMessage;
                return {
                    status: response.status,
                    message: errorMessage,
                    errors: response.data || response.data?.errors || {},
                };
            },
        }),
    }),
    overrideExisting: false,
});

export const {
    usePostPatientInformationMutation,
    useGetPatientInformationQuery,
    useGetPatientEntitlementsQuery,
} = patientInformationApi;
